class GotQuestions extends HTMLElement {
  constructor() {
    super();
    this.drawerId = 'GotQuestionsDrawer';
    this.overlayId = 'Drawer-Overlay';
    this.unreadStorageKey = 'wpi_chat_unread';
    /** @type {boolean} */
    this._appContentMounted = false;
    /** @type {HTMLAudioElement|null} */
    this._notificationAudio = null;
    /** @type {number} */
    this._lastSoundAt = 0;
    /** @type {boolean} */
    this._pendingSound = false;
    /** @type {AudioContext|null} */
    this._audioCtx = null;
    /** @type {AudioBuffer|null} */
    this._audioBuffer = null;
    /** @type {Promise|null} */
    this._audioBufferPromise = null;
    /** @type {number|null} */
    this._titleFlashTimer = null;
    /** @type {string|null} */
    this._originalTitle = null;
    /** @type {Notification|null} */
    this._browserNotification = null;
  }

  connectedCallback() {
    this.ensureDrawer();
    this.bindGlobalTriggers();
    this.bindChatEvents();
    this.bindAudioUnlock();
    // Mount the app content as soon as the page is ready instead of waiting for the drawer to
    // open, and wait for the same point to badge triggers rendered after this element.
    const onReady = () => {
      this.mountAppContentOnce();
      this.renderUnreadBadge();
    };
    if (document.readyState === 'complete') {
      onReady();
    } else {
      window.addEventListener('load', onReady, { once: true });
    }
  }

  ensureDrawer() {
    if (document.getElementById(this.drawerId)) return;

    const drawer = document.createElement('div');
    drawer.className = 'drawer drawer__overlay';
    drawer.id = this.drawerId;
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    drawer.innerHTML = `
      <div class="drawer__inner drawer__inner-scroll-off custom-scrollbar gradient color-scheme-2 motion-reduce" aria-labelledby="GotQuestionsTitle">
        <div class="drawer__header pl-2 pr-2" style="justify-content: start;gap: 2rem;">
          <svg class="icon icon--chat_bubble standard-icon" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 20 20"><path d="M5.80023 10.2433C5.80023 9.691 6.24795 9.24329 6.80023 9.24329C7.35252 9.24329 7.80023 9.691 7.80023 10.2433C7.80023 10.7956 7.35252 11.2433 6.80023 11.2433C6.24795 11.2433 5.80023 10.7956 5.80023 10.2433Z"></path>
            <path d="M9.22974 10.2433C9.22974 9.691 9.67745 9.24329 10.2297 9.24329C10.782 9.24329 11.2297 9.691 11.2297 10.2433C11.2297 10.7956 10.782 11.2433 10.2297 11.2433C9.67745 11.2433 9.22974 10.7956 9.22974 10.2433Z"></path>
            <path d="M12.6592 10.2433C12.6592 9.691 13.1069 9.24329 13.6592 9.24329C14.2115 9.24329 14.6592 9.691 14.6592 10.2433C14.6592 10.7956 14.2115 11.2433 13.6592 11.2433C13.1069 11.2433 12.6592 10.7956 12.6592 10.2433Z"></path>
            <path d="M16.6445 17.2036C16.6418 17.2023 16.6392 17.2009 16.6365 17.1996C16.3947 17.0764 16.1302 16.9257 15.8701 16.7774C15.2251 16.4099 14.607 16.0576 14.4279 16.1795C13.2314 16.9937 11.7861 17.4696 10.2297 17.4696C6.10323 17.4696 2.75806 14.1244 2.75806 9.99791C2.75806 5.87142 6.10323 2.52625 10.2297 2.52625C14.3562 2.52625 17.7014 5.87142 17.7014 9.99791C17.7014 11.512 17.251 12.9209 16.477 14.0979C16.3445 14.2994 16.6705 14.9403 17.0113 15.6104C17.1583 15.8994 17.308 16.1938 17.4249 16.4607C17.4269 16.4653 17.4289 16.4697 17.4308 16.4742C17.6237 16.9172 17.7239 17.2824 17.5671 17.418C17.4394 17.5544 17.0791 17.4243 16.6445 17.2036ZM18.2593 18.14C17.8792 18.5115 17.4018 18.4853 17.2128 18.461C16.9786 18.4309 16.7562 18.3504 16.587 18.2799C16.2391 18.1348 15.8412 17.9114 15.506 17.7209C15.4611 17.6954 15.4172 17.6704 15.3743 17.6459C15.0837 17.4804 14.8356 17.3391 14.6299 17.2385C13.347 18.0196 11.8396 18.4696 10.2297 18.4696C5.55095 18.4696 1.75806 14.6767 1.75806 9.99791C1.75806 5.31914 5.55095 1.52625 10.2297 1.52625C14.9085 1.52625 18.7014 5.31914 18.7014 9.99791C18.7014 11.5821 18.2656 13.0672 17.5072 14.3369C17.5098 14.3432 17.5125 14.3497 17.5153 14.3564C17.6071 14.5755 17.7411 14.8392 17.8997 15.1513C17.927 15.2049 17.9549 15.2599 17.9835 15.3164C18.1621 15.6689 18.3719 16.0905 18.5003 16.4618C18.5631 16.6435 18.6314 16.8815 18.6427 17.1309C18.6529 17.3561 18.6228 17.7987 18.2593 18.14Z" fill-rule="evenodd"></path>
          </svg>
          <h2 id="GotQuestionsTitle" class="h4 m-0"></h2>
          <button type="button" class="drawer__close" aria-label="New chat" data-start-new-chat title="New chat" style="right: 7rem;box-shadow: 0px 0px 9px 0.2rem rgb(0 0 0 / 8%);border-radius: 50%;">
            <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14"/>
            </svg>
          </button>
          <button type="button" class="drawer__close" aria-label="Close" data-close-got-questions>
            <svg viewBox="0 0 16 14" width="26" height="26" fill="none" aria-hidden="true">
              <path d="M1.027 1.324a.75.75 0 0 1 1.06 0L8 7.237l5.913-5.913a.75.75 0 0 1 1.06 1.06L9.06 8.297l5.913 5.913a.75.75 0 1 1-1.06 1.06L8 9.358l-5.913 5.912a.75.75 0 1 1-1.06-1.06l5.912-5.913-5.912-5.913a.75.75 0 0 1 0-1.06Z" fill="currentColor"/>
            </svg>
          </button>
        </div>
        <div class="drawer__contents drawer__inner-scroll-always" style="display:flex;flex-direction:column;">
          <div class="got-questions-drawer__body" style="flex:1; overflow:auto;"></div>
        </div>
      </div>
    `;

    document.body.appendChild(drawer);
    const titleEl = drawer.querySelector('#GotQuestionsTitle');
    if (titleEl) titleEl.textContent = this.dataset.drawerTitle || '';

    // Close handlers
    drawer.addEventListener('click', (e) => {
      const inner = drawer.querySelector('.drawer__inner');
      if (!inner) return;
      const clickInsideInner = inner.contains(e.target);
      if (!clickInsideInner) this.close();
    });
    drawer.querySelector('[data-close-got-questions]')?.addEventListener('click', () => this.close());
    drawer.querySelector('[data-start-new-chat]')?.addEventListener('click', () => {
      if (typeof window.wpiStartNewChat === 'function') window.wpiStartNewChat();
    });

    // Overlay click closes (use existing overlay if present)
    const overlay = document.getElementById(this.overlayId);
    if (overlay) overlay.addEventListener('click', () => this.close());
  }

  bindGlobalTriggers() {
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-open-got-questions]');
      if (trigger) {
        e.preventDefault();
        this.requestNotificationPermission();
        this.open();
      }
      const closer = e.target.closest('[data-close-got-questions]');
      if (closer) {
        e.preventDefault();
        this.close();
      }
      const startNewChat = e.target.closest('[data-start-new-chat]');
      if (startNewChat && typeof window.wpiStartNewChat === 'function') {
        e.preventDefault();
        window.wpiStartNewChat();
      }
    });
  }

  /**
   * The chat widget emits `wpi-chat:*` events from #wpi-chat-root, which bubble up
   * to the document even though the widget lives inside the drawer.
   */
  bindChatEvents() {
    document.addEventListener('wpi-chat:message:received', (e) => {
      this.playNotificationSound();
      this.setUnread(true);
      // Nothing to grab attention with while the customer is already looking at the tab.
      if (document.hidden) {
        this.startTitleFlash();
        this.showBrowserNotification(e.detail?.content || '');
      }
    });
    // A reply only counts as read once the customer has actually seen the bubble.
    document.addEventListener('wpi-chat:message:read', (e) => {
      if ((e.detail?.role || 'assistant') !== 'assistant') return;
      this.setUnread(false);
    });
    document.addEventListener('wpi-chat:chat:reset', () => this.setUnread(false));
    // A dynamically initiated conversation opens the drawer so the greeting is seen.
    document.addEventListener('wpi-chat:chat:initiated', () => {
      this.playNotificationSound();
      this.open();
    });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) return;
      // A chat the assistant started on its own is usually blocked from playing,
      // so the sound is held until the customer is back on the tab.
      this.flushPendingSound();
      this.clearAttentionGrabbers();
    });
  }

  /** Alternates the tab title so the new message is visible from another tab. */
  startTitleFlash() {
    if (this._titleFlashTimer) return;
    const alertTitle = this.dataset.newMessageTitle || 'New message';
    this._originalTitle = document.title;
    document.title = alertTitle;
    let showingAlert = true;
    this._titleFlashTimer = window.setInterval(() => {
      showingAlert = !showingAlert;
      document.title = showingAlert ? alertTitle : this._originalTitle;
    }, 1200);
  }

  stopTitleFlash() {
    if (!this._titleFlashTimer) return;
    window.clearInterval(this._titleFlashTimer);
    this._titleFlashTimer = null;
    if (this._originalTitle !== null) document.title = this._originalTitle;
    this._originalTitle = null;
  }

  clearAttentionGrabbers() {
    this.stopTitleFlash();
    if (this._browserNotification) {
      try { this._browserNotification.close(); } catch (e) {}
      this._browserNotification = null;
    }
  }

  /**
   * Asked for from the first gesture, so the prompt is tied to a real interaction
   * (which Safari requires) and so notifications also work for a customer who
   * never opened the drawer themselves.
   */
  requestNotificationPermission() {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'default') return;
    try {
      Notification.requestPermission()?.catch(() => {});
    } catch (e) {}
  }

  showBrowserNotification(content) {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    const body = String(content).replace(/\s+/g, ' ').trim();
    try {
      const notification = new Notification(this.dataset.newMessageTitle || 'New message', {
        body: body.length > 120 ? body.slice(0, 119) + '\u2026' : body,
        icon: this.dataset.notificationIcon || undefined,
        // A single tag keeps repeated replies collapsed into one notification.
        tag: 'wpi-chat-message',
        renotify: true
      });
      notification.addEventListener('click', () => {
        window.focus();
        this.clearAttentionGrabbers();
        this.open();
      });
      this._browserNotification = notification;
    } catch (e) {}
  }

  /** Survives a page refresh so the badge stays until the message is read. */
  hasUnread() {
    try {
      return window.localStorage?.getItem(this.unreadStorageKey) === 'true';
    } catch (e) {}
    return false;
  }

  setUnread(unread) {
    if (this.hasUnread() === unread) return;
    try {
      if (unread) {
        window.localStorage?.setItem(this.unreadStorageKey, 'true');
      } else {
        window.localStorage?.removeItem(this.unreadStorageKey);
      }
    } catch (e) {}
    this.renderUnreadBadge();
  }

  renderUnreadBadge() {
    const unread = this.hasUnread();
    document.querySelectorAll('[data-open-got-questions]').forEach((trigger) => {
      if (unread) {
        trigger.setAttribute('data-unread', 'true');
      } else {
        trigger.removeAttribute('data-unread');
      }
    });
  }

  getNotificationAudio() {
    const src = this.dataset.notificationSound;
    if (!src) return null;
    if (!this._notificationAudio) {
      this._notificationAudio = new Audio(src);
      this._notificationAudio.preload = 'auto';
    }
    return this._notificationAudio;
  }

  /**
   * An <audio> element is only allowed to play right after a gesture, so a chat the
   * assistant starts on its own is muted. A Web Audio context unlocked on the first
   * gesture stays usable afterwards, including while the tab is in the background,
   * so the clip is decoded up front and played through it.
   */
  setupWebAudio() {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return false;
    if (!this._audioCtx) {
      try {
        this._audioCtx = new Ctx();
      } catch (e) {
        return false;
      }
    }
    if (this._audioCtx.state === 'suspended') this._audioCtx.resume()?.catch(() => {});
    this.loadAudioBuffer();
    return true;
  }

  loadAudioBuffer() {
    if (this._audioBuffer || this._audioBufferPromise) return this._audioBufferPromise;
    const src = this.dataset.notificationSound;
    if (!src || !this._audioCtx) return null;
    this._audioBufferPromise = fetch(src)
      .then((response) => response.arrayBuffer())
      .then((data) => this._audioCtx.decodeAudioData(data))
      .then((buffer) => {
        this._audioBuffer = buffer;
        return buffer;
      })
      .catch(() => {
        // Falls back to the <audio> element.
        this._audioBufferPromise = null;
        return null;
      });
    return this._audioBufferPromise;
  }

  playViaWebAudio() {
    const ctx = this._audioCtx;
    if (!ctx || !this._audioBuffer) return false;
    if (ctx.state === 'suspended') ctx.resume()?.catch(() => {});
    if (ctx.state !== 'running') return false;
    try {
      const source = ctx.createBufferSource();
      source.buffer = this._audioBuffer;
      source.connect(ctx.destination);
      source.start(0);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * The first gesture is the only chance to unlock audio and the only moment a
   * permission prompt is allowed, so both are done together.
   */
  bindAudioUnlock() {
    const events = ['pointerdown', 'keydown', 'touchstart'];
    const unlock = () => {
      events.forEach((name) => document.removeEventListener(name, unlock, true));
      this.requestNotificationPermission();

      if (!this.setupWebAudio()) {
        // Playing silently marks the element as user-approved for later plays.
        const audio = this.getNotificationAudio();
        if (audio) {
          audio.muted = true;
          try {
            const played = audio.play();
            played?.then(() => {
              audio.pause();
              audio.currentTime = 0;
              audio.muted = false;
            }).catch(() => { audio.muted = false; });
          } catch (e) {
            audio.muted = false;
          }
        }
      }

      this.flushPendingSound();
    };
    events.forEach((name) => document.addEventListener(name, unlock, { capture: true, passive: true }));
  }

  /** Replays a sound the browser refused, as long as the message is still unread. */
  flushPendingSound() {
    if (!this._pendingSound) return;
    this._pendingSound = false;
    if (!this.hasUnread()) return;
    this._lastSoundAt = 0;
    this.playNotificationSound();
  }

  playNotificationSound() {
    // An initiated chat emits both message:received and chat:initiated back to back.
    const now = Date.now();
    if (now - this._lastSoundAt < 500) return;
    this._lastSoundAt = now;

    if (this.playViaWebAudio()) return;

    const audio = this.getNotificationAudio();
    if (!audio) return;
    try {
      audio.currentTime = 0;
      // Nothing has unlocked audio yet, so the sound is held for the next chance.
      audio.play()?.catch(() => { this._pendingSound = true; });
    } catch (e) {
      this._pendingSound = true;
    }
  }

  mountAppContentOnce() {
    if (this._appContentMounted) return;
    const drawer = document.getElementById(this.drawerId);
    const body = drawer?.querySelector('.got-questions-drawer__body');
    const template = this.querySelector('template[data-got-questions-app]');
    if (body && template?.content?.childElementCount) {
      body.appendChild(template.content);
    }
    this._appContentMounted = true;
  }

  open() {
    this.clearAttentionGrabbers();
    this.ensureDrawer();
    this.mountAppContentOnce();
    const drawer = document.getElementById(this.drawerId);
    if (!drawer) return;
    drawer.classList.add('active');
    const overlay = document.getElementById(this.overlayId);
    if (overlay) overlay.classList.add('active');
    document.body.classList.add('overflow-hidden');
  }

  close() {
    const drawer = document.getElementById(this.drawerId);
    if (!drawer) return;
    drawer.classList.remove('active');
    const overlay = document.getElementById(this.overlayId);
    if (overlay) overlay.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
  }
}

if (!customElements.get('got-questions')) {
  customElements.define('got-questions', GotQuestions);
}


